// Balance Sheet PDF component for PDF generation using @react-pdf/renderer
import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: 30,
  },
  header: {
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#002FA7",
    paddingBottom: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#002FA7",
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 12,
    color: "#666666",
  },
  organization: {
    marginBottom: 15,
    fontSize: 14,
    fontWeight: "bold",
  },
  customerInfo: {
    marginBottom: 15,
    fontSize: 12,
    color: "#666666",
  },
  summarySection: {
    marginBottom: 20,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  summaryCard: {
    width: "23%",
    padding: 10,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 4,
  },
  summaryLabel: {
    fontSize: 10,
    color: "#666666",
    marginBottom: 5,
    textTransform: "uppercase",
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: "bold",
  },
  table: {
    marginBottom: 10,
  },
  tableHeader: {
    backgroundColor: "#F3F4F6",
    flexDirection: "row",
  },
  tableCell: {
    padding: 8,
    fontSize: 10,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  tableRow: {
    flexDirection: "row",
  },
  dateCell: {
    width: "15%",
  },
  descriptionCell: {
    width: "30%",
  },
  customerCell: {
    width: "20%",
  },
  amountCell: {
    width: "12%",
    textAlign: "right",
  },
  balanceCell: {
    width: "13%",
    textAlign: "right",
  },
  outstandingCell: {
    width: "13%",
    textAlign: "right",
  },
  dueDateCell: {
    width: "12%",
  },
  statusCell: {
    width: "10%",
  },
  referenceCell: {
    width: "8%",
  },
  debitText: {
    color: "#DC2626",
    fontWeight: "bold",
  },
  creditText: {
    color: "#059669",
    fontWeight: "bold",
  },
  footer: {
    marginTop: "auto",
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    fontSize: 10,
    color: "#666666",
    textAlign: "center",
  },
});

const BalanceSheetPdf = ({ data }) => {
  const { organization, customer, entries, summary, dateRange } = data;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Balance Sheet</Text>
          <Text style={styles.subtitle}>
            {dateRange?.from && dateRange?.to 
              ? `${dateRange.from} to ${dateRange.to}`
              : "All Time"
            }
          </Text>
        </View>

        {/* Organization Info */}
        {organization && (
          <View style={styles.organization}>
            <Text>{organization.organization_name}</Text>
            {organization.email && <Text style={{ fontSize: 10 }}>{organization.email}</Text>}
            {organization.phone && <Text style={{ fontSize: 10 }}>{organization.phone}</Text>}
          </View>
        )}

        {/* Customer Info */}
        {customer && (
          <View style={styles.customerInfo}>
            <Text>Customer: {customer.name}</Text>
            {customer.company && <Text>{customer.company}</Text>}
          </View>
        )}

        {/* Summary Cards */}
        {summary && (
          <View style={styles.summarySection}>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Total Billed</Text>
              <Text style={styles.summaryValue}>₹{Number(summary.total_debit || 0).toLocaleString("en-IN")}</Text>
            </View>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Total Collected</Text>
              <Text style={styles.summaryValue}>₹{Number(summary.total_credit || 0).toLocaleString("en-IN")}</Text>
            </View>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Current Balance</Text>
              <Text style={[styles.summaryValue, summary.current_balance > 0 && { color: "#DC2626" }]}>
                ₹{Number(summary.current_balance || 0).toLocaleString("en-IN")}
              </Text>
            </View>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Overdue</Text>
              <Text style={[styles.summaryValue, { color: "#EA580C" }]}>
                ₹{Number(summary.overdue_amount || 0).toLocaleString("en-IN")}
              </Text>
            </View>
          </View>
        )}

        {/* Ledger Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <View style={[styles.tableCell, styles.dateCell]}>
              <Text>Date</Text>
            </View>
            <View style={[styles.tableCell, styles.descriptionCell]}>
              <Text>Description</Text>
            </View>
            <View style={[styles.tableCell, styles.customerCell]}>
              <Text>Customer</Text>
            </View>
            <View style={[styles.tableCell, styles.amountCell]}>
              <Text>Debit</Text>
            </View>
            <View style={[styles.tableCell, styles.amountCell]}>
              <Text>Credit</Text>
            </View>
            <View style={[styles.tableCell, styles.outstandingCell]}>
              <Text>Outstanding</Text>
            </View>
            <View style={[styles.tableCell, styles.dueDateCell]}>
              <Text>Due Date</Text>
            </View>
            <View style={[styles.tableCell, styles.statusCell]}>
              <Text>Status</Text>
            </View>
            <View style={[styles.tableCell, styles.referenceCell]}>
              <Text>Ref</Text>
            </View>
          </View>
          
          {/* Table Body */}
          {entries?.map((entry) => (
            <View key={entry.id} style={styles.tableRow}>
              <View style={[styles.tableCell, styles.dateCell]}>
                <Text>{entry.entry_date}</Text>
              </View>
              <View style={[styles.tableCell, styles.descriptionCell]}>
                <Text>{entry.description || "—"}</Text>
              </View>
              <View style={[styles.tableCell, styles.customerCell]}>
                <Text>{entry.customer_name || "—"}</Text>
              </View>
              <View style={[styles.tableCell, styles.amountCell]}>
                {entry.entry_type === "debit" ? (
                  <Text style={styles.debitText}>₹{Number(entry.amount).toLocaleString("en-IN")}</Text>
                ) : (
                  <Text>—</Text>
                )}
              </View>
              <View style={[styles.tableCell, styles.amountCell]}>
                {entry.entry_type === "credit" ? (
                  <Text style={styles.creditText}>₹{Number(entry.amount).toLocaleString("en-IN")}</Text>
                ) : (
                  <Text>—</Text>
                )}
              </View>
              <View style={[styles.tableCell, styles.outstandingCell]}>
                {entry.reference_type === "invoice" ? (
                  <Text style={entry.outstanding > 0 ? styles.debitText : entry.outstanding < 0 ? styles.creditText : {}}>
                    ₹{Number(entry.outstanding || 0).toLocaleString("en-IN")}
                  </Text>
                ) : (
                  <Text>—</Text>
                )}
              </View>
              <View style={[styles.tableCell, styles.dueDateCell]}>
                <Text>{entry.due_date || "—"}</Text>
              </View>
              <View style={[styles.tableCell, styles.statusCell]}>
                {entry.reference_type === "invoice" ? (
                  <Text style={{
                    fontSize: 9,
                    color: entry.calculated_status === "paid" ? "#059669" : 
                           entry.calculated_status === "overdue" ? "#DC2626" : 
                           entry.calculated_status === "cancelled" ? "#6B7280" : 
                           "#CA8A04"
                  }}>
                    {entry.calculated_status?.toUpperCase() || entry.invoice_status?.toUpperCase() || "PENDING"}
                  </Text>
                ) : (
                  <Text style={{ fontSize: 9, color: "#6B7280" }}>—</Text>
                )}
              </View>
              <View style={[styles.tableCell, styles.referenceCell]}>
                <Text style={{ fontSize: 8 }}>{entry.reference_number || "—"}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>Generated by BillForge • {new Date().toLocaleDateString()}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default BalanceSheetPdf;
